import {
  EngagementFeedWithUser,
  EngagementMedia,
  NewsfeedItemResponseDto,
} from '@epicuro-next/data/engagement/engagement-models';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { ActiveReference } from '../interfaces/active-reference';

import * as actions from './actions';

/**
 * State slice key
 */
export const NEWS_FEED_STATE_KEY = 'news-feed';

/**
 * News feed state type
 */
export interface NewsFeedState extends EntityState<EngagementFeedWithUser> {
  /** Articles total count(on a server) */
  total: number | null;
  /** State property for loading in action */
  isLoading: boolean;
  /** Current page */
  page: number;
  loadFailed: boolean;
  activeReference: ActiveReference | null;
}

/** State adapter */
export const adapter: EntityAdapter<EngagementFeedWithUser> =
  createEntityAdapter<EngagementFeedWithUser>({
    selectId: (article: EngagementFeedWithUser) => article.id,
  });

/**
 * Initial state for the news feed
 */
export const initialState: NewsFeedState = adapter.getInitialState({
  total: null,
  isLoading: false,
  page: 0,
  loadFailed: false,
  activeReference: null,
});

/**
 * Reducer for news feed related actions
 */
const newsFeedReducer = createReducer(
  initialState,
  on(actions.fetch, (state) => {
    return { ...state, isLoading: true, loadFailed: false };
  }),
  on(actions.fetchFail, actions.fetchSuccess, (state) => {
    return {
      ...state,
      isLoading: false,
    };
  }),
  on(actions.fetchFail, (state) => {
    return {
      ...state,
      loadFailed: !Boolean(state.total),
    };
  }),
  on(actions.fetchSuccess, (state, action) => {
    return adapter.upsertMany(action.data.items, {
      ...state,
      total: action.data.total,
      page: state.page + 1,
      loadFailed: false,
    });
  }),
  on(actions.fetchUpdatesSuccess, (state, action) => {
    return adapter.upsertMany(action.data.items, {
      ...state,
      total: (state.total ?? 0) + action.data.total,
    });
  }),
  on(
    actions.deleteArticleSuccess,
    actions.editArticleRemoveFromTodaySpotlight,
    (state, action) => {
      return adapter.removeOne(action.feedId, state);
    },
  ),
  on(actions.deleteArticlesBulk, (state, action) => {
    return adapter.removeMany(action.ids, state);
  }),
  on(actions.editArticleSuccess, (state, action) => {
    return adapter.updateOne(
      {
        id: action.article.id,
        changes: action.article,
      },
      state,
    );
  }),
  on(actions.editArticlePromoteReferencesSuccess, (state, action) => {
    return adapter.updateOne(
      {
        id: action.id,
        changes: {
          promoteReferences: action.promoteReferences,
        },
      },
      state,
    );
  }),
  on(actions.renameArticleSuccess, (state, { id, title }) => {
    return adapter.updateOne(
      {
        id: id as string,
        changes: {
          title,
        },
      },
      state,
    );
  }),
  on(actions.getArticleSuccess, (state, action) => {
    return adapter.upsertOne(action.article, state);
  }),
  on(actions.pinArticleSuccess, (state, action) => {
    return adapter.updateOne(
      {
        id: action.id,
        changes: {
          isPinned: true,
        },
      },
      state,
    );
  }),
  on(actions.unpinArticleSuccess, (state, action) => {
    return adapter.updateOne(
      {
        id: action.id,
        changes: {
          isPinned: false,
        },
      },
      state,
    );
  }),
  on(actions.reset, () => ({
    ...initialState,
  })),
  on(actions.setActiveReference, (state, action) => {
    return {
      ...state,
      activeReference: action.reference,
    };
  }),
  on(actions.partialUpdate, (state, action) => {
    return adapter.updateOne(
      {
        id: action.article.id as string,
        changes: {
          ...action.article,
        },
      },
      state,
    );
  }),
  on(actions.editListArticle, (state, { list }) => {
    return adapter.map((entity) => {
      const { name, description } = list;
      const existingList = entity.references?.some(
        (reference) => reference?.id === list.id,
      );

      const references = existingList
        ? entity.references?.map((reference) =>
            reference?.id === list.id
              ? { ...reference, name, description: description as string }
              : reference,
          )
        : entity.references;

      return {
        ...entity,
        references: references as unknown as (NewsfeedItemResponseDto & {
          media: EngagementMedia;
        })[],
      };
    }, state);
  }),
  on(actions.updateCurrentUserImage, (state, { userId, url }) =>
    adapter.map(
      (entity) =>
        entity.userId === userId
          ? { ...entity, userProfilePictureUrl: url }
          : entity,
      state,
    ),
  ),
);

/** News feed reducer */
export function reducer(state: NewsFeedState | undefined, action: Action) {
  return newsFeedReducer(state, action);
}
